<script setup>
import {ref, computed, onMounted, watch} from 'vue'
import {useTimeAgo} from '@vueuse/core'
import {Toast as BootstrapToast} from 'bootstrap'
import ProgressBar from "@/vue/common/ProgressBar"
import UserAvatar from "@/common/UserAvatar"

const props = defineProps({
    createdBy: Number,
    createdDate: Number,
    title: String,
    body: String,
    disableAutoHide: Boolean,
    downloadUrl: String,
    percentage: Number,
    colourScheme: String,
    disableAnimation: {
        type: Boolean,
        default: false
    },
    disableClose: {
        type: Boolean,
        default: false
    },
    moreInfo: String,
    onClose: Function,
    closeOnComplete: Boolean,
    closeOnDownload: Boolean
})

// --- Refs for Element & Instance ---
const toastElRef = ref(null)
const toastInstance = ref(null)

const timeAgo = useTimeAgo(props.createdDate * 1000)

// --- Computed ---
const containerColourClass = computed(() => {
    return props.colourScheme ? `text-bg-${props.colourScheme} border-0` : ''
})

const buttonColourClass = computed(() => {
    if (['primary', 'danger'].includes(props.colourScheme)) {
        return 'btn-close-white'
    }
    return ''
})

// --- Lifecycle (Mounted) ---
onMounted(() => {
    if (toastElRef.value) {
        // Instantiate the Bootstrap Toast utility
        toastInstance.value = new BootstrapToast(toastElRef.value, {
            autohide: !props.disableAutoHide,
            animation: !props.disableAnimation
        })

        toastInstance.value.show()

        if (props.onClose) {
            toastElRef.value.addEventListener('hidden.bs.toast', () => {
                props.onClose()
            })
        }
    }
})

// --- Methods ---
const downloadExport = () => {
    window.location = props.downloadUrl

    if (props.closeOnDownload && toastInstance.value) {
        toastInstance.value.hide()
    }
}

// --- Watchers ---
watch(() => props.percentage, (newValue) => {
    if (newValue === 100 && props.closeOnComplete && toastInstance.value) {
        setTimeout(() => {
            if (toastInstance.value) {
                toastInstance.value.hide()
            }
        }, 3000)
    }
})
</script>

<template>
    <div ref="toastElRef" class="toast" :class="containerColourClass" role="alert" aria-live="assertive"
         aria-atomic="true">
        <template v-if="title">
            <div class="toast-header">
                <UserAvatar v-if="createdBy" :user-id="createdBy" class="me-2"/>

                <strong class="me-auto">{{ title }}</strong>

                <small v-if="createdDate" class="text-muted">{{ timeAgo }}</small>

                <button v-if="!disableClose" type="button" class="btn-close small pe-2" :class="buttonColourClass"
                        data-bs-dismiss="toast" aria-label="Close"></button>
            </div>

            <div class="toast-body">
                <div v-html="body"></div>

                <a href v-if="downloadUrl" @click.prevent="downloadExport">Download</a>

                <ProgressBar v-if="percentage >= 0 && percentage < 100" :percentage="percentage" class="mt-1"/>

                <div v-if="moreInfo" v-html="moreInfo" class="mt-2 py-1 px-2 border rounded bg-light small"></div>
            </div>
        </template>

        <div v-else class="d-flex">
            <div class="toast-body" v-html="body"></div>

            <button v-if="!disableClose" type="button" class="btn-close me-2 m-auto" :class="buttonColourClass"
                    data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
    </div>
</template>